import React from 'react';
import { CheckBoxParam } from '../../types/CheckBoxParam';
import { ElementContainer } from '../../grid/ElementContainer/ElementContainer';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { InputProps } from '../../../../types/HtmlProps';
import bem from '../../../../lib/bemUtils/bem';
import { Modifier } from '../../../../lib/bemUtils/Modifier';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { RequiredSymbol } from '../../partials/RequiredSymbol/RequiredSymbol';
import { Help } from '../../partials/Help/Help';

type Switch = {
  label: string;
  helpText?: string;
  helpTextPlacement?: Help['helpTextPlacement'];
  modifierLabel?: Modifier<'position-inherit'>;
  param?: CheckBoxParam<string>;
  onClick?: () => void;
} & Omit<InputProps, 'id' | 'className' | 'type'>;

function normalizeParamsToHTMLAttributes(htmlInputProps: InputProps, param?: CheckBoxParam<string>): InputProps {
  return {
    name: param?.name,
    required: param?.required,
    defaultValue: param?.value,
    defaultChecked: param?.checked,
    ...htmlInputProps,
  };
}

export function Switch({
  label,
  helpText,
  helpTextPlacement,
  param,
  modifierLabel,
  onClick,
  ...htmlInputProps
}: Switch) {
  const { t } = useSonTranslation('FormLabel');
  const id = useHtmlValidId();
  const switchId = `switch-${id}`;
  const helpTextId = `switch-help-text-${id}`;

  const htmlAttributes = normalizeParamsToHTMLAttributes(htmlInputProps, param);

  return (
    <ElementContainer>
      <span className="switch">
        <input
          className="switch__input"
          id={switchId}
          type="checkbox"
          aria-describedby={helpText && helpTextId}
          onClick={onClick}
          {...htmlAttributes}
        />
        <span className="switch__label-container">
          <label className={bem('switch__label', modifierLabel)} htmlFor={switchId}>
            {label}
            {htmlAttributes.required && <RequiredSymbol />}
            <span
              className="switch__label-text font-regular-13-21"
              aria-hidden="true"
              data-switch-on={t('on')}
              data-switch-off={t('off')}
            />
          </label>
          {helpText && <Help id={helpTextId} helpText={helpText} helpTextPlacement={helpTextPlacement} />}
        </span>
      </span>
    </ElementContainer>
  );
}
